import { useMutation, useQuery } from "react-query"
import { Viewer, Differ, DiffResult } from 'json-diff-kit'
import { Accomodation, PromoAssociation } from "../../../types"
import { associatePromo, deleteAccomodationPromo, getAccommodationPromo, getAccomodationPromoHistory, getAllPromo } from "../../../shared/queries"
import FormSectionTitle from "../../form/FormSectionTitle"
import Modal from "../../common/Modal"
import ModalCard from "../../common/ModalCard"
import { useEffect, useState } from "react"
import dayjs from "dayjs"
import SelectInput from "../../form/SelectInput"
import FormField from "../../form/FormField"
import DatePickerInput from "../../form/DatePickerInput"
import toast from "react-hot-toast"
import utc from 'dayjs/plugin/utc'

import 'json-diff-kit/dist/viewer.css'

dayjs.extend(utc)

const differ = new Differ({
  detectCircular: true,    // default `true`
  maxDepth: Infinity,      // default `Infinity`
  showModifications: true, // default `true`
  arrayDiffMethod: 'lcs',  // default `"normal"`, but `"lcs"` may be more useful
})

const PromoHistory = ({ accommodation, currentStatus } : { accommodation: Accomodation, currentStatus: PromoAssociation | undefined }) => {

  const { data } = useQuery(['promoHistory', accommodation.idAccomodation], () => getAccomodationPromoHistory(accommodation.idAccomodation))

  const [difference, setDifference] = useState<readonly [DiffResult[], DiffResult[]]>()
  const [status, setStatus] = useState<{
    before: any,
    after: any
  }>()

  return (
    <div>
      <Modal visible={status !== undefined}>
        <ModalCard
          title="Storico modifica"
          onClose={() => {
            setDifference(undefined)
            setStatus(undefined)
          }}
        >
          {
            difference &&
            <Viewer
              diff={difference}
              indent={4}
              lineNumbers={true}         // default `false`
              highlightInlineDiff={true} // default `false`
              inlineDiffOptions={{
                mode: 'word',            // default `"char"`, but `"word"` may be more useful
                wordSeparator: ' ',      // default `""`, but `" "` is more useful for sentences
              }}
            />
          }
        </ModalCard>
      </Modal>

      <div className="flex flex-col space-y-2">
        {
          data && data.map((d: PromoAssociation, i: any) => {
            return (
              <div onClick={() => {
                if (!data) return

                let status = undefined

                if (i === 0) {
                  status = {
                    before: {
                      ...data[i],
                      accommodation: null,
                      promo: null
                    },
                    after: {
                      ...currentStatus,
                      accommodation: null,
                      promo: null
                    },
                  }
                } else {
                  status = {
                    after: {
                      ...data[i],
                      accommodation: null,
                      promo: null
                    },
                    before: {
                      ...data[i + 1],
                      accommodation: null,
                      promo: null
                    }
                  }
                }

                setStatus(status)
                setDifference(differ.diff(status.before, status.after))
              }} className="p-2 border bg-white shadow-sm rounded-md">
                Modifica {d.idPromoAccommodation}
              </div>
            )
          })
        }
      </div>
    </div>
  )

}

const PromoModal = ({ accommodation, currentPromo, onCreated } : { accommodation: Accomodation, currentPromo: PromoAssociation | undefined, onCreated: () => void }) => {

  const { data } = useQuery(['promo'], () => getAllPromo())
  
  const [idPromo, setIdPromo] = useState<number | undefined>()
  const [enrollmentDate, setEnrollmentDate] = useState<Date>(dayjs().add(1, 'day').toDate())
  const [effectiveStartDate, setEffectiveStartDate] = useState<Date | undefined>()
  const [effectiveEndDate, setEffectiveEndDate] = useState<Date | undefined>()

  useEffect(() => {
    if (currentPromo) {
      setIdPromo(currentPromo.promo.idPromo)
      setEnrollmentDate(dayjs(currentPromo.enrollmentDate).toDate())
      setEffectiveStartDate(dayjs(currentPromo.effectiveStartDate).toDate())
      setEffectiveEndDate(dayjs(currentPromo.effectiveEndDate).toDate())
    }
  }, [ currentPromo ])

  const associateMutation = useMutation((data: PromoAssociation) => associatePromo(data), {
    onSuccess: () => {
      toast.success('Promozione associata')
    },
    onError: (data: any) => {
      toast.error(`Errore associazione: ${data.message}`)
    }
  })

  return (
    <Modal visible={currentPromo !== undefined}>
      <ModalCard
        className="w-full max-w-xl"
        title={`Associa promozione`}
        onSave={() => {
          if (!accommodation || !accommodation.idAccomodation || !idPromo) return
          associateMutation.mutate({
            idPromoAccommodation: currentPromo?.idPromoAccommodation || undefined,
            accommodation: {
              idAccomodation: accommodation.idAccomodation
            },
            promo: {
                idPromo: idPromo
            },
            enrollmentDate: dayjs(enrollmentDate).format('YYYY-MM-DDTHH:mm:ss'),
            effectiveStartDate: dayjs(effectiveStartDate).format('YYYY-MM-DDTHH:mm:ss'),
            effectiveEndDate: dayjs(effectiveEndDate).format('YYYY-MM-DDTHH:mm:ss')
          })
          onCreated()
        }}
        onClose={() => {
          onCreated()
        }}
      >
        <div className="p-4 flex flex-col space-y-4">
          <FormField label="Promozione" required={true}>
            <SelectInput
              value={data?.map(p => {
                return { label: p.promoCode, value: p.idPromo }
              }).find(p => p.value === idPromo)}
              menuShouldScrollIntoView={false}
              menuPortalTarget={document.body}
              styles={{
                container: (base: any) => ({ ...base, zIndex: 9999 }),
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
              }}
              onChange={(val: any) => {
                if (val) {
                  setIdPromo(val.value)
                  const selectedPromo = data?.find(p => p.idPromo === val.value)
                    if (selectedPromo) {
                      setEffectiveStartDate(dayjs().add(selectedPromo.includeCurrentMonth ? 0 : 1, 'months').startOf('month').toDate())
                      setEffectiveEndDate(dayjs().add(selectedPromo.cashbackInvoices, 'months').endOf('month').toDate())
                    }
                }
              }}
              options={data?.map(promo => {
                return {
                  label: `${promo.promoCode}`,
                  value: promo.idPromo
                }
              })}
            />
          </FormField>

          {
            (idPromo) &&
            <>
              <FormField label={'Data inizio promozione'}>
                <DatePickerInput
                  mode={'single'}
                  value={effectiveStartDate}
                  onChange={(val) => {
                      if (val) {
                        setEffectiveStartDate(val)
                      }
                  }}
                />
              </FormField>

              <FormField label={'Data fine promozione'}>
                <DatePickerInput
                  mode={'single'}
                  value={effectiveEndDate}
                  onChange={(val) => {
                      if (val) {
                        setEffectiveEndDate(val)
                      }
                  }}
                />
              </FormField>
            </>
          }
        </div>

      </ModalCard>
    </Modal>
  )

}

export default function HotelPromo ({ accomodation } : {
  accomodation: Accomodation
}) {

  const [currentPromo, setCurrentPromo] = useState<undefined | PromoAssociation>()

  const {
    data,
    refetch
  } = useQuery(['accommodationPromo', accomodation.idAccomodation], () => getAccommodationPromo(accomodation.idAccomodation || 0))

  const deleteAssociateMutation = useMutation((id: number | undefined) => deleteAccomodationPromo(id), {
    onSuccess: () => {
      refetch()
      toast.success('Promozione rimossa')
    },
    onError: (data: any) => {
      refetch()
      toast.error(`Errore associazione: ${data.message}`)
    }
  })

  return (
    <div>
      <FormSectionTitle
        borderless
        title={'Promozioni associate'}
        subtitle={'puoi associare una sola promozione alla struttura ricettiva'}
        edit={true}
        editLabel={'Associa promozione'}
        onEdit={() => {
          setCurrentPromo({
            accommodation: {
                idAccomodation: accomodation.idAccomodation || 0
            },
            promo: {
              idPromo: undefined
            },
            enrollmentDate: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
            effectiveStartDate: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
            effectiveEndDate: dayjs().format('YYYY-MM-DDTHH:mm:ss')
          })
        }}
      />

      <PromoModal accommodation={accomodation} currentPromo={currentPromo} onCreated={() => {
        setCurrentPromo(undefined)
        refetch()
      }} />

      {
        (!data) && <>Nessuna promozione associata</>
      }

      {
        (data && data.idPromoAccommodation) &&
        <div className="bg-white border p-4 shadow-xl rounded-md flex justify-between items-center">
          <div>
            <div className="font-bold text-slate-700">{data.promo.promoCode}</div>
            <div className="text-slate-500">{data.promo.description}</div>
          </div>
          <div className="flex space-x-4 items-center">
            <button className="font-medium text-orange-600" onClick={() => setCurrentPromo(data)}>
              Modifica
            </button>
            <button onClick={() => deleteAssociateMutation.mutate(data.idPromoAccommodation)} className="bg-red-50 font-medium text-red-600 p-1 px-2 rounded-md">
              Dissocia
            </button>
          </div>
        </div>
      }

      <FormSectionTitle
        borderless
        title={'Storico delle modifiche'}
        subtitle={'log storico delle modifiche alle assocazioni'}
      />

      <PromoHistory accommodation={accomodation} currentStatus={data} />

    </div>
  )

}